import { APIError } from "@/lib/api";

export type ProviderLaunchRecoveryJob = {
  id?: string;
  launch_request?: Record<string, unknown>;
};

type ProviderLaunchRecoveryOptions = {
  attempts?: number;
  delayMs?: number;
  wait?: (delayMs: number) => Promise<void>;
};

export function providerLaunchOutcomeMayBeUnknown(error: unknown) {
  if (!(error instanceof APIError)) return false;
  return (
    error.status === 408 ||
    error.status >= 500 ||
    error.code === "ERR_TIMEOUT" ||
    error.code === "ERR_NETWORK"
  );
}

export function providerJobForClientLaunchID<T extends ProviderLaunchRecoveryJob>(
  jobs: T[],
  clientLaunchID: string,
) {
  const expected = String(clientLaunchID || "").trim();
  if (!expected) return null;
  return (
    jobs.find((job) => String(job.launch_request?.client_launch_id || "").trim() === expected) ||
    null
  );
}

export async function recoverProviderJobLaunch<T extends ProviderLaunchRecoveryJob>(
  clientLaunchID: string,
  loadJobs: () => Promise<T[]>,
  options: ProviderLaunchRecoveryOptions = {},
) {
  const attempts = Math.max(1, Math.round(options.attempts ?? 4));
  const delayMs = Math.max(0, Math.round(options.delayMs ?? 750));
  const wait =
    options.wait ??
    ((duration) => new Promise<void>((resolve) => window.setTimeout(resolve, duration)));
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const recovered = providerJobForClientLaunchID(await loadJobs(), clientLaunchID);
      if (recovered) return recovered;
    } catch {
      // A recovery read is best-effort; the next poll may reach the durable job.
    }
    if (attempt + 1 < attempts && delayMs > 0) {
      await wait(delayMs);
    }
  }
  return null;
}
